import React, { useState, useMemo } from 'react';
import { Box, Button, Checkbox, CircularProgress, ListItemText, MenuItem, Select, alpha } from '@mui/material';
import { useGetTags } from '../portalQueryApi/queryTagsApi';

// --- helpers to put "----untagged----" first + add a divider ---
const UNTAGGED_ID = '----untagged----';
const isUntagged = (o) => o?.entityId === UNTAGGED_ID || (typeof o?.tagValue === 'string' && o.tagValue.trim().toLowerCase() === 'untagged');

const orderUntaggedFirst = (arr = []) => {
  const untagged = [];
  const rest = [];
  arr.forEach((o) => (isUntagged(o) ? untagged.push(o) : rest.push(o)));
  untagged.sort((a, b) => a.tagValue.localeCompare(b.tagValue));
  rest.sort((a, b) => a.tagValue.localeCompare(b.tagValue));
  return [...untagged, ...rest];
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
// MapTagSelect: returns '' when "Untagged" is selected; accepts '' in value.
// format: 'value' (default) or 'id'
export const MapTagSelect = (props) => {
  const { value, handleChange, name, sx, changeImediate = false, filterByUserTags = false, format = 'value' } = props;
  const { data: allOptions, status } = useGetTags({ entityType: 'Tags', filterByUserTags });

  // Normalize incoming values based on format
  const normalizeIn = (vals) => {
    const arr = Array.isArray(vals) ? vals : vals ? [vals] : [];
    if (format === 'id') {
      // map '' (untagged) to sentinel id to match filterTags contract
      return arr.map((v) => (v === '' ? UNTAGGED_ID : String(v)));
    }
    // 'value' mode: keep '' for untagged
    return arr.map((v) => (v == null ? '' : String(v)));
  };

  const [iValues, setIValues] = useState(normalizeIn(value));
  // keep in sync if parent changes `value` or `format`
  React.useEffect(() => {
    setIValues(normalizeIn(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, format]);

  const sortedOptions = useMemo(() => orderUntaggedFirst(allOptions || []), [allOptions]);

  // token we store/emit for each option depends on format:
  //  - 'value': '' for Untagged, tagValue for others
  //  - 'id': UNTAGGED_ID for Untagged, entityId for others
  const tokenFor = (opt) => {
    if (format === 'id') return isUntagged(opt) ? UNTAGGED_ID : String(opt?.entityId ?? '');
    return isUntagged(opt) ? '' : String(opt?.tagValue ?? '');
  };
  const hasToken = (arr, opt) => Array.isArray(arr) && arr.includes(tokenFor(opt));

  if (status === 'pending') return <CircularProgress />;
  if (status === 'error') return <span>Error loading</span>;

  const handleClose = () => {
    const e = { target: { value: iValues, name } };
    if (!changeImediate && handleChange) handleChange(e);
  };

  const onChange = (e) => {
    const { value } = e.target;
    const val = typeof value === 'string' ? value.split(',') : value;
    setIValues(val);
    if (changeImediate && handleChange) handleChange(e);
  };

  const handleSetAll = (e) => {
    e.stopPropagation();
    const newTags = sortedOptions.map((opt) => tokenFor(opt)); // includes '' for Untagged
    setIValues(newTags);
    const param = { target: { value: newTags, name } };
    if (changeImediate && handleChange) handleChange(param);
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    setIValues([]);
    const param = { target: { value: [], name } };
    if (changeImediate && handleChange) handleChange(param);
  };

  const totalTokens = sortedOptions.map(tokenFor).length;

  return (
    <Select
      id='map-tag-select'
      color='controls'
      name={name}
      multiple
      size='small'
      sx={[
        {
          color: (theme) => theme.palette.primary.main,
          borderRadius: '8px',
          height: { md: '40px', xs: '32px' },
          // width: '60px',
          '& .MuiInputBase-input': { px: 1, py: 1, lineHeight: '24px', fontSize: '16px' },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
          },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: (theme) => theme.palette.primary.main },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: '1px solid',
            borderColor: (theme) => theme.palette.primary.main,
          },
          '& .MuiSelect-icon': { color: (theme) => theme.palette.primary.main, right: 4 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      displayEmpty={true}
      renderValue={(selected) => {
        if (selected?.length === totalTokens) return 'Tags: All';
        if (!selected || selected?.length === 0) return 'Tags';
        return `Tags (${selected.length})`;
      }}
      value={iValues}
      onChange={onChange}
      onClose={handleClose}
      MenuProps={{
        PaperProps: {
          style: { borderRadius: '8px' },
          sx: {
            background: (theme) => theme.palette.background.main,
            '& .MuiMenuItem-root': {
              '&:hover': { backgroundColor: (theme) => theme.palette.background.light5 },
            },
            '&.Mui-selected': { backgroundColor: (theme) => theme.palette.primary.main },
          },
        },
      }}
    >
      {sortedOptions.map((opt) => {
        const token = tokenFor(opt); // '' for Untagged
        return (
          <MenuItem
            key={opt.entityId}
            value={token}
            sx={{
              pl: '4px',
              py: '2px',
              '& .MuiTypography-root': { fontSize: '14px' },
              '&:hover': { backgroundColor: (theme) => theme.palette.primary.main },
              ...(isUntagged(opt) && {
                borderBottom: '1px solid',
                borderColor: (theme) => theme.palette.divider,
              }),
            }}
          >
            <Box sx={{ height: '36px', display: 'flex', alignItems: 'center' }}>
              <Checkbox checked={hasToken(iValues, opt)} size='20px' sx={{ py: 0.5 }} />
              <ListItemText sx={{ fontSize: '14px' }} primary={opt.tagValue} />
            </Box>
          </MenuItem>
        );
      })}
      <MenuItem
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '200px',
          p: 1,
          '& .MuiTypography-root': { fontSize: '14px' },
          '&:hover': { background: 'none' },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
          <Button size='small' onClick={handleSetAll} sx={{ width: '50%', mx: 1, '&:hover': { backgroundColor: 'none' } }}>
            Set All
          </Button>
          <Button size='small' onClick={handleClearAll} sx={{ width: '50%', mx: 1, '&:hover': { backgroundColor: 'none' } }}>
            Clear All
          </Button>
        </Box>
      </MenuItem>
    </Select>
  );
};
